/**
 * Validation utilities for form fields with dynamic min/max constraints
 */

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates a numeric value against min/max constraints from attribute metadata
 * @param value - The value to validate
 * @param attribute - The attribute object containing validation rules
 * @returns Validation result with error message if invalid
 */
export function validateNumericAttribute(
  value: number | string | null | undefined,
  attribute: any
): ValidationResult {
  // Skip validation if no value provided
  if (value === null || value === undefined || value === "") {
    return { isValid: true };
  }

  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  // Check if value is a valid number
  if (Number.isNaN(numericValue)) {
    return {
      isValid: false,
      errorMessage: "Please enter a valid number",
    };
  }

  const { possibleMinValue, possibleMaxValue, name, friendlyName } = attribute;
  const fieldName = friendlyName || name || "This field";

  // Validate minimum value
  if (possibleMinValue !== null && possibleMinValue !== undefined) {
    if (numericValue < possibleMinValue) {
      return {
        isValid: false,
        errorMessage: `${fieldName} must be at least ${possibleMinValue.toLocaleString()}`,
      };
    }
  }

  // Validate maximum value
  if (possibleMaxValue !== null && possibleMaxValue !== undefined) {
    if (numericValue > possibleMaxValue) {
      return {
        isValid: false,
        errorMessage: `${fieldName} cannot exceed ${possibleMaxValue.toLocaleString()}`,
      };
    }
  }

  return { isValid: true };
}

/**
 * Validates all numeric attributes in a form object
 * @param formData - Object containing form field values
 * @param attributes - Array of attribute metadata
 * @returns Object mapping field IDs to validation results
 */
export function validateNumericFields(
  formData: Record<string, any>,
  attributes: any[]
): Record<string, ValidationResult> {
  const validationResults: Record<string, ValidationResult> = {};

  attributes.forEach((attribute) => {
    const { _id, dataType } = attribute;

    // Only validate numeric/integer fields with min/max constraints
    if (
      (dataType === "Integer" || dataType === "Real") &&
      (attribute.possibleMinValue !== null ||
        attribute.possibleMaxValue !== null)
    ) {
      const value = formData[_id];
      validationResults[_id] = validateNumericAttribute(value, attribute);
    }
  });

  return validationResults;
}

/**
 * Formats a number input to prevent exceeding max value
 * @param value - Input value
 * @param maxValue - Maximum allowed value
 * @returns Formatted value within bounds
 */
export function enforceMaxValue(
  value: string | number,
  maxValue: number
): number {
  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return 0;

  return Math.min(numericValue, maxValue);
}

/**
 * Formats a number input to prevent going below min value
 * @param value - Input value
 * @param minValue - Minimum allowed value
 * @returns Formatted value within bounds
 */
export function enforceMinValue(
  value: string | number,
  minValue: number
): number {
  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return minValue;

  return Math.max(numericValue, minValue);
}

/**
 * Formats a number input to stay within min/max bounds
 * @param value - Input value
 * @param minValue - Minimum allowed value
 * @param maxValue - Maximum allowed value
 * @returns Formatted value within bounds
 */
export function enforceMinMaxValue(
  value: string | number,
  minValue: number | null | undefined,
  maxValue: number | null | undefined
): number {
  let numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return 0;

  if (minValue !== null && minValue !== undefined) {
    numericValue = Math.max(numericValue, minValue);
  }

  if (maxValue !== null && maxValue !== undefined) {
    numericValue = Math.min(numericValue, maxValue);
  }

  return numericValue;
}
